import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HousePrototypeProps, Hotspot } from '../types';
import Tooltip from './Tooltip';
import HotspotLegend from './HotspotLegend';
import { useAccessibility } from '../hooks/useAccessibility';

const HousePrototype: React.FC<HousePrototypeProps> = ({
  illustration,
  onHotspotClick,
  selectedHotspot,
  highlightedHotspotIds = []
}) => {
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [hoveredHotspot, setHoveredHotspot] = useState<Hotspot | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const { announce } = useAccessibility();

  // Detect mobile device
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleHotspotClick = (hotspot: Hotspot, event: React.MouseEvent) => {
    event.preventDefault();
    
    if (containerRef.current) {
      const x = event.clientX; // Use viewport coordinates
      const y = event.clientY; // Use viewport coordinates
      
      setTooltipPosition({ x, y });
      setShowTooltip(true);
      onHotspotClick(hotspot);
      announce(`Selected ${hotspot.title} hotspot`);
    }
  };

  const handleHotspotHover = (hotspot: Hotspot, event: React.MouseEvent) => {
    if (!isMobile && !showTooltip) { // Only hover if no tooltip is currently selected
      if (containerRef.current) {
        const x = event.clientX; // Use viewport coordinates
        const y = event.clientY; // Use viewport coordinates
        
        setTooltipPosition({ x, y });
        setHoveredHotspot(hotspot);
        announce(`Hovering over ${hotspot.title} hotspot`);
      }
    }
  };

  const handleHotspotLeave = () => {
    if (!isMobile && !showTooltip) { // Only clear hover if no tooltip is selected
      setHoveredHotspot(null);
    }
  };

  const handleHotspotKeyDown = (hotspot: Hotspot, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleHotspotClick(hotspot, event as any);
    }
  };

  const closeTooltip = () => {
    console.log('closeTooltip called!');
    setShowTooltip(false);
    setHoveredHotspot(null);
    announce('Tooltip closed');
  };

  // Close tooltip when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      // Check if click is on tooltip or its children
      if (target.closest('.tooltip')) {
        return; // Don't close if clicking on tooltip
      }
      
      // Check if click is on hotspot
      if (target.closest('.hotspot')) {
        return; // Don't close if clicking on hotspot
      }
      
      // Check if click is on backdrop
      if (target.id === 'tooltip-backdrop') {
        return; // Let backdrop handle this
      }
      
      // Close tooltip when clicking anywhere outside
      console.log('Click outside detected, closing tooltip');
      setShowTooltip(false);
      setHoveredHotspot(null);
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        console.log('Escape key pressed, closing tooltip');
        setShowTooltip(false);
        setHoveredHotspot(null);
      }
    };

    if (showTooltip && (selectedHotspot || hoveredHotspot)) {
      // Use mousedown for more immediate response
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [showTooltip, selectedHotspot, hoveredHotspot]);

  // Add backdrop overlay for better UX
  useEffect(() => {
    if (showTooltip && (selectedHotspot || hoveredHotspot)) {
      // Add backdrop
      const backdrop = document.createElement('div');
      backdrop.className = 'fixed inset-0 bg-black bg-opacity-20 z-40';
      backdrop.style.pointerEvents = 'auto';
      backdrop.id = 'tooltip-backdrop';
      
      const handleBackdropClick = (e: Event) => {
        console.log('Backdrop clicked!');
        e.preventDefault();
        e.stopPropagation();
        setShowTooltip(false);
        setHoveredHotspot(null);
      };
      
      backdrop.addEventListener('click', handleBackdropClick);
      backdrop.addEventListener('mousedown', handleBackdropClick);
      document.body.appendChild(backdrop);
      
      return () => {
        backdrop.removeEventListener('click', handleBackdropClick);
        backdrop.removeEventListener('mousedown', handleBackdropClick);
        if (document.body.contains(backdrop)) {
          document.body.removeChild(backdrop);
        }
      };
    }
  }, [showTooltip, selectedHotspot, hoveredHotspot]);

  return (
    <div className="relative w-full max-w-4xl mx-auto">
      <div
        ref={containerRef}
        className="relative bg-white rounded-lg shadow-lg overflow-hidden"
        role="img"
        aria-label={`${illustration.name} - ${illustration.description}`}
      >
        {/* House Illustration */}
        <div className="relative w-full">
          <img 
            ref={imageRef}
            src="/assets/waterarmor1.png" 
            alt={`${illustration.name} - ${illustration.description}`}
            className="w-full h-auto object-contain max-h-[600px]"
          />
        </div>

        {/* Hotspots */}
        {illustration.hotspots.map((hotspot) => {
          const isHighlighted = highlightedHotspotIds.includes(hotspot.id);
          return (
            <motion.button
              key={hotspot.id}
              className={`hotspot ${isHighlighted ? 'highlighted-hotspot' : ''}`}
              style={{
                left: `${hotspot.x}%`,
                top: `${hotspot.y}%`,
                transform: 'translate(-50%, -50%)',
                backgroundColor: isHighlighted ? '#f59e0b' : undefined,
                borderColor: isHighlighted ? '#fbbf24' : undefined,
                boxShadow: isHighlighted ? '0 0 0 4px rgba(245, 158, 11, 0.3), 0 0 20px rgba(245, 158, 11, 0.5)' : undefined,
              }}
              onClick={(e) => handleHotspotClick(hotspot, e)}
              onMouseEnter={(e) => handleHotspotHover(hotspot, e)}
              onMouseLeave={handleHotspotLeave}
              onKeyDown={(e) => handleHotspotKeyDown(hotspot, e)}
              aria-label={`${hotspot.title} - ${isMobile ? 'Tap' : 'Hover or click'} for more information`}
              aria-describedby={`hotspot-${hotspot.id}-description`}
              whileHover={{ scale: isMobile ? 1 : 1.1 }}
              whileTap={{ scale: 0.95 }}
              initial={{ scale: 0 }}
              animate={{ 
                scale: 1,
                backgroundColor: isHighlighted ? '#f59e0b' : undefined,
              }}
              transition={{ delay: 0.5 + Math.random() * 0.5 }}
            >
              <span className="sr-only" id={`hotspot-${hotspot.id}-description`}>
                {hotspot.description}
              </span>
            </motion.button>
          );
        })}

        {/* Tooltip */}
        {(showTooltip && (selectedHotspot || hoveredHotspot)) && (
          <Tooltip
            hotspot={selectedHotspot || hoveredHotspot!}
            isVisible={true}
            position={tooltipPosition}
            onClose={closeTooltip}
          />
        )}
      </div>

      {/* Enhanced Hotspot Legend */}
      <div className="mt-8">
        <HotspotLegend 
          hotspots={illustration.hotspots}
          onHotspotSelect={(hotspot) => {
            // Scroll to hotspot and trigger click
            const hotspotElement = document.querySelector(`[aria-label*="${hotspot.title}"]`);
            if (hotspotElement) {
              hotspotElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              (hotspotElement as HTMLElement).focus();
              // Trigger the hotspot click
              onHotspotClick(hotspot);
            }
          }}
          selectedHotspot={selectedHotspot}
        />
      </div>
    </div>
  );
};

export default HousePrototype;
